import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiLoadingModalService, UiLoadingModalVariant } from './loading-modal.service';

export {
  UiLoadingModalService,
  type UiLoadingModalOptions,
  type UiLoadingModalVariant,
} from './loading-modal.service';

/**
 * Molécula: modal de retroalimentación con cuatro estados visuales.
 *
 * - `loading` — spinner animado + título + subtítulo (sin botones).
 * - `success` — SVG animado (pop + draw-stroke) en verde.
 * - `error`   — SVG animado (shake + draw-stroke) en rojo.
 * - `warning` — SVG animado (pulse + draw-stroke) en ámbar.
 *
 * En variantes no-loading puede mostrar:
 *   - Un botón principal (`showButton`, `confirmLabel`, `confirmDanger`).
 *   - Un botón secundario de cancelación (`showCancelButton`, `cancelLabel`).
 *
 * Puede controlarse de dos formas:
 *   1. Declarativa — vía inputs en el template.
 *   2. Programática — inyectando `UiLoadingModalService`.
 *
 * Los inputs tienen prioridad sobre el servicio cuando se definen explícitamente.
 */
@Component({
  selector: 'ui-loading-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent],
  templateUrl: './loading-modal.component.html',
  styleUrl: './loading-modal.component.scss',
})
export class UiLoadingModalComponent {
  /** Visibilidad del modal. `null` delega al servicio. */
  readonly open = input<boolean | null>(null);

  /** Estado visual. `null` delega al servicio (defecto: `'loading'`). */
  readonly variant = input<UiLoadingModalVariant | null>(null);

  /** Título principal en negrita. `null` delega al servicio. */
  readonly title = input<string | null>(null);

  /** Texto descriptivo bajo el título. `null` delega al servicio. */
  readonly message = input<string | null>(null);

  /** Label del botón principal. `null` delega al servicio. */
  readonly confirmLabel = input<string | null>(null);

  /** Muestra u oculta el botón principal. `null` delega al servicio. */
  readonly showButton = input<boolean | null>(null);

  /** Muestra un botón secundario de cancelación. `null` delega al servicio. */
  readonly showCancelButton = input<boolean | null>(null);

  /** Label del botón secundario. `null` delega al servicio. */
  readonly cancelLabel = input<string | null>(null);

  /** Aplica estilo de peligro (rojo) al botón principal. `null` delega al servicio. */
  readonly confirmDanger = input<boolean | null>(null);

  /**
   * Si `true`, el backdrop no cierra el modal y se bloquean
   * scroll, ESC y gestos táctiles. `null` delega al servicio.
   */
  readonly blockUi = input<boolean | null>(null);

  /** Emite cuando el usuario pulsa el botón principal en estado `success`. */
  readonly confirmed = output<void>();

  /** Emite cuando el usuario pulsa el botón principal en estados `error` / `warning`, o cuando cierra por backdrop. */
  readonly closed = output<void>();

  /** Emite cuando el usuario pulsa el botón secundario de cancelación. */
  readonly cancelClicked = output<void>();

  private readonly svc = inject(UiLoadingModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isVisible = computed(() =>
    this.open() !== null ? !!this.open() : this.svc.isOpen(),
  );

  protected readonly currentVariant = computed(
    () => this.variant() ?? this.svc.variant(),
  );

  protected readonly currentTitle = computed(
    () => this.title() ?? this.svc.title(),
  );

  protected readonly currentMessage = computed(
    () => this.message() ?? this.svc.message(),
  );

  protected readonly currentConfirmLabel = computed(
    () => this.confirmLabel() ?? this.svc.confirmLabel(),
  );

  protected readonly currentShowButton = computed(() =>
    this.showButton() !== null ? !!this.showButton() : this.svc.showButton(),
  );

  protected readonly currentShowCancelButton = computed(() =>
    this.showCancelButton() !== null
      ? !!this.showCancelButton()
      : this.svc.showCancelButton(),
  );

  protected readonly currentCancelLabel = computed(
    () => this.cancelLabel() ?? this.svc.cancelLabel(),
  );

  protected readonly currentConfirmDanger = computed(() =>
    this.confirmDanger() !== null
      ? !!this.confirmDanger()
      : this.svc.confirmDanger(),
  );

  protected readonly isBlocked = computed(() =>
    this.blockUi() !== null ? !!this.blockUi() : this.svc.blockUi(),
  );

  /** Clase del wrapper; cambia con el variant para re-lanzar animaciones CSS. */
  protected readonly iconWrapClass = computed(
    () => `ui-lm__icon-wrap ui-lm__icon-wrap--${this.currentVariant()}`,
  );

  /** Shortcuts para @if en el template (fuerza re-creación del SVG al cambiar variant). */
  /** Nombre accesible cuando la variante no muestra título. */
  protected readonly dialogLabel = computed(() => {
    switch (this.currentVariant()) {
      case 'success':
        return 'Éxito';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Aviso';
      default:
        return 'Cargando';
    }
  });

  protected readonly isSuccess = computed(() => this.currentVariant() === 'success');
  protected readonly isError   = computed(() => this.currentVariant() === 'error');
  protected readonly isWarning = computed(() => this.currentVariant() === 'warning');

  /** true cuando hay al menos un botón visible fuera del estado loading */
  protected readonly hasActions = computed(() =>
    this.currentVariant() !== 'loading' &&
    (this.currentShowButton() || this.currentShowCancelButton()),
  );

  constructor() {
    // Se usa effect() y no afterRender() porque necesitamos reaccionar
    // síncronamente al cambio de visibilidad para bloquear el scroll ANTES
    // de que el usuario pueda interactuar. Esta app es client-only (sin SSR),
    // por lo que el acceso a document.body es seguro en este contexto.
    effect(() => {
      document.body.style.overflow = this.isVisible() ? 'hidden' : '';
    });

    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = '';
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEsc(event: Event): void {
    if (this.isVisible() && this.isBlocked()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('document:wheel', ['$event'])
  protected onWheel(event: Event): void {
    if (this.isVisible() && this.isBlocked()) {
      event.preventDefault();
    }
  }

  @HostListener('document:touchmove', ['$event'])
  protected onTouchMove(event: Event): void {
    if (this.isVisible() && this.isBlocked()) {
      event.preventDefault();
    }
  }

  protected onBackdropClick(): void {
    if (this.isBlocked()) return;
    this.svc.close();
    this.closed.emit();
  }

  protected onConfirm(): void {
    this.svc.close();
    if (this.currentVariant() === 'success') {
      this.confirmed.emit();
    } else {
      this.closed.emit();
    }
  }

  protected onCancel(): void {
    this.svc.close();
    this.cancelClicked.emit();
  }
}
